/** 外部アプリ由来の HTML 表 / TSV を、キャンバスの表セルへ変換する。 */

import { formatMarkdownLink } from "@refinement-canvas/domain";

export interface ParsedClipboardTable {
  readonly cells: readonly (readonly string[])[];
  readonly truncated: boolean;
}

interface TableLimits {
  readonly maxColumns: number;
  readonly maxRows: number;
}

interface ExpandedTable extends ParsedClipboardTable {
  readonly score: number;
}

const SKIPPED_TAGS = new Set(["head", "meta", "noscript", "script", "style", "template", "title"]);

function normalizedSpan(raw: string | null, limit: number): { value: number; truncated: boolean } {
  const normalized = raw?.trim() ?? "1";
  const parsed = /^\d+$/u.test(normalized) ? Number(normalized) : 1;
  if (parsed === Number.POSITIVE_INFINITY) return { value: limit, truncated: true };
  if (!Number.isFinite(parsed) || parsed <= 0) return { value: 1, truncated: false };
  return { value: Math.min(parsed, limit), truncated: parsed > limit };
}

function isSafeLink(href: string): boolean {
  try {
    const protocol = new URL(href).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

function serializeChildren(node: Node): string {
  let text = "";
  node.childNodes.forEach((child) => {
    text += serializeNode(child);
  });
  return text;
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replace(/\s+/gu, " ");
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const element = node as HTMLElement;
  const tag = element.tagName.toLowerCase();
  if (SKIPPED_TAGS.has(tag)) return "";
  if (tag === "br") return " ";
  if (tag === "img") return element.getAttribute("alt") ?? "";

  const inner = serializeChildren(element);
  const normalized = inner.replace(/\s+/gu, " ");
  const trimmed = normalized.trim();
  if (trimmed.length === 0) return "";
  const leadingSpace = normalized.startsWith(" ") ? " " : "";
  const trailingSpace = normalized.endsWith(" ") ? " " : "";
  const decorate = (opening: string, closing = opening): string =>
    `${leadingSpace}${opening}${trimmed}${closing}${trailingSpace}`;

  if (tag === "a") {
    const href = (element.getAttribute("href") ?? "").trim();
    return isSafeLink(href)
      ? `${leadingSpace}${formatMarkdownLink(trimmed, href)}${trailingSpace}`
      : inner;
  }
  if (tag === "code") return decorate("`");
  if (tag === "s" || tag === "del" || tag === "strike") return decorate("~~");
  if (tag === "em" || tag === "i") return decorate("*");

  const fontWeight = element.style.fontWeight;
  const numericWeight = Number.parseInt(fontWeight, 10);
  if (
    tag === "strong" ||
    tag === "b" ||
    fontWeight.toLowerCase() === "bold" ||
    (Number.isFinite(numericWeight) && numericWeight >= 600)
  ) {
    return decorate("**");
  }
  return inner;
}

function serializeCell(cell: Element): string {
  return serializeChildren(cell).replace(/\s+/gu, " ").trim();
}

function directRows(table: HTMLTableElement): HTMLTableRowElement[] {
  return Array.from(table.rows).filter(
    (row): row is HTMLTableRowElement => row.closest("table") === table
  );
}

function directCells(row: HTMLTableRowElement): HTMLTableCellElement[] {
  return Array.from(row.cells);
}

function expandTable(table: HTMLTableElement, limits: TableLimits): ExpandedTable {
  const rows = directRows(table);
  const output: string[][] = [];
  const occupiedUntil = new Map<number, number>();
  let maxWidth = 0;
  let truncated = rows.length > limits.maxRows;

  rows.forEach((row, rowIndex) => {
    let column = 0;
    let rowWidth = 0;
    const outputRow = rowIndex < limits.maxRows ? Array<string>(limits.maxColumns).fill("") : null;

    for (const cell of directCells(row)) {
      const colspan = normalizedSpan(cell.getAttribute("colspan"), limits.maxColumns);
      const rowspan = normalizedSpan(cell.getAttribute("rowspan"), limits.maxRows);
      truncated ||= colspan.truncated || rowspan.truncated;

      while ((occupiedUntil.get(column) ?? 0) > rowIndex) column += 1;
      while (
        Array.from({ length: colspan.value }).some(
          (_, offset) => (occupiedUntil.get(column + offset) ?? 0) > rowIndex
        )
      ) {
        column += 1;
        while ((occupiedUntil.get(column) ?? 0) > rowIndex) column += 1;
      }

      if (column >= limits.maxColumns) {
        truncated = true;
      }

      const endColumn = column + colspan.value;
      if (endColumn > limits.maxColumns) truncated = true;
      if (outputRow !== null && column < limits.maxColumns) outputRow[column] = serializeCell(cell);
      for (let current = column; current < endColumn; current += 1) {
        if (rowspan.value > 1) occupiedUntil.set(current, rowIndex + rowspan.value);
      }
      rowWidth = Math.max(rowWidth, endColumn);
      column = endColumn;
    }

    for (const [occupiedColumn, endRow] of occupiedUntil) {
      if (endRow > rowIndex) rowWidth = Math.max(rowWidth, occupiedColumn + 1);
      else occupiedUntil.delete(occupiedColumn);
    }
    maxWidth = Math.max(maxWidth, rowWidth);
    if (outputRow !== null) output.push(outputRow);
  });

  const cells = output.map((row) => row.slice(0, Math.min(maxWidth, limits.maxColumns)));
  return {
    cells,
    truncated,
    score: Math.min(Number.MAX_SAFE_INTEGER, rows.length * maxWidth),
  };
}

function hasContentOutsideTable(body: HTMLElement, selectedTable: HTMLTableElement): boolean {
  const visit = (node: Node): boolean => {
    if (node === selectedTable) return false;
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").trim().length > 0;
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const element = node as Element;
    if (element.tagName.toLowerCase() === "img") return true;
    return Array.from(node.childNodes).some(visit);
  };
  return visit(body);
}

function isUsableTable(table: ParsedClipboardTable): boolean {
  const rowCount = table.cells.length;
  const columnCount = table.cells[0]?.length ?? 0;
  if (rowCount === 0 || columnCount === 0 || (rowCount === 1 && columnCount === 1)) return false;
  return table.cells.some((row) => row.some((cell) => cell.length > 0));
}

/** HTML 内で最大の表を、結合セルとインライン書式を保った矩形セルへ変換する。 */
export function htmlTableToCells(html: string, limits: TableLimits): ParsedClipboardTable | null {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let selected: { table: HTMLTableElement; expanded: ExpandedTable } | undefined;
    for (const table of Array.from(doc.querySelectorAll("table"))) {
      const expanded = expandTable(table, limits);
      if (selected === undefined || expanded.score > selected.expanded.score) {
        selected = { table, expanded };
      }
    }
    if (selected === undefined || hasContentOutsideTable(doc.body, selected.table)) return null;
    return isUsableTable(selected.expanded)
      ? { cells: selected.expanded.cells, truncated: selected.expanded.truncated }
      : null;
  } catch {
    return null;
  }
}

/** タブ区切りの複数行テキストを矩形セルへ変換する。 */
export function tsvToCells(text: string, limits: TableLimits): ParsedClipboardTable | null {
  const lines = text.replace(/\r\n?/gu, "\n").split("\n");
  while (lines.at(-1) === "") lines.pop();
  if (lines.length < 2 || lines.some((line) => !line.includes("\t"))) return null;

  const rows = lines.map((line) => line.split("\t"));
  const columnCount = rows[0]?.length ?? 0;
  if (columnCount < 2 || rows.some((row) => row.length !== columnCount)) return null;

  const truncated = rows.length > limits.maxRows || columnCount > limits.maxColumns;
  const cells = rows
    .slice(0, limits.maxRows)
    .map((row) => row.slice(0, limits.maxColumns).map((cell) => cell.trim()));
  return cells.some((row) => row.some((cell) => cell.length > 0)) ? { cells, truncated } : null;
}
